import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { openPaywall } from "@/shared/lib/paywall";
import { Badge, Popover, ProviderIcon, Spinner, Text } from "@/shared/ui";
import { useMe } from "../hooks/useMe";
import { useModels, useModelUsage } from "../hooks/useModels";
import {
  filterModels,
  groupModelsByOrigin,
  isFreeModel,
  modelAllowedForPlan,
  type ModelInfo,
  modelLabel,
  originLabel,
  type PlanTier,
  recentModels,
  requiredPlanFor,
} from "../lib/models";
import { CheckIcon, ChevronDownIcon, LockIcon, SearchIcon } from "./icons";

export interface ModelPickerProps {
  value: string | null;
  onChange: (modelId: string) => void;
  side?: "top" | "bottom";
}

/**
 * The composer model selector: a pill trigger + a searchable panel listing
 * recently-used models then the full catalog grouped by provider. Capabilities,
 * tier, and plan show as monochrome badges; cost is the per-turn credit estimate.
 */
export function ModelPicker({ value, onChange, side = "top" }: ModelPickerProps) {
  const { t } = useTranslation();
  const { data: modelsData, isPending, isError } = useModels();
  const { data: usageData } = useModelUsage();
  const { data: me } = useMe();
  const [query, setQuery] = useState("");

  const plan: PlanTier = me?.plan_id ?? "free";
  const models = useMemo(() => modelsData?.items ?? [], [modelsData]);
  const usage = useMemo(() => usageData?.items ?? [], [usageData]);
  const selected = models.find((m) => m.id === value) ?? null;

  const recents = useMemo(() => recentModels(models, usage), [models, usage]);
  const filtered = useMemo(() => filterModels(models, query), [models, query]);
  const groups = useMemo(() => groupModelsByOrigin(filtered), [filtered]);
  const showRecents = query.trim() === "" && recents.length > 0;

  /**
   * Picking a model the current plan can't use opens the paywall instead of
   * switching to it — so the lock is felt at click time, not when the send
   * later 402s. Allowed models switch as before.
   */
  const selectModel = (model: ModelInfo, close: () => void): void => {
    if (modelAllowedForPlan(model, plan)) {
      onChange(model.id);
      close();
      return;
    }
    close();
    const required = requiredPlanFor(model);
    if (required) {
      openPaywall({ kind: "model", modelName: modelLabel(model), plan: required });
    }
  };

  return (
    <Popover
      side={side}
      align="start"
      label={t("models.selectModel")}
      panelClassName="w-[360px]"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          data-testid="model-selector"
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="inline-flex h-9 max-w-[200px] items-center gap-2 rounded-pill bg-bubble px-3 font-sans text-[13px] font-medium text-ink outline-none transition-colors duration-150 ease-house hover:bg-tint focus-visible:bg-tint"
        >
          {selected ? (
            <ProviderIcon origin={selected.origin} className="size-4 shrink-0 text-ink" />
          ) : (
            <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-ink" />
          )}
          <span className="truncate">{selected ? modelLabel(selected) : t("models.selectModel")}</span>
          <ChevronDownIcon className="size-3.5 shrink-0 text-ink-soft" />
        </button>
      )}
    >
      {({ close }) => (
        <div className="flex min-h-0 flex-col">
          <div className="flex items-center gap-2 px-3 pb-1 pt-2">
            <SearchIcon className="size-3.5 shrink-0 text-ink-soft" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("models.searchModels")}
              aria-label={t("models.searchModels")}
              className="w-full bg-transparent font-sans text-[13px] text-ink outline-none placeholder:text-ink-faint"
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {isPending ? (
              <div className="flex items-center gap-2 px-3 py-3">
                <Spinner size={14} />
                <Text variant="body" className="text-[13px] text-ink-faint">
                  {t("models.loading")}
                </Text>
              </div>
            ) : isError ? (
              <Text variant="body" className="px-3 py-3 text-[13px] text-ink-soft">
                {t("errors.somethingWentWrong")}
              </Text>
            ) : filtered.length === 0 ? (
              <Text variant="body" className="px-3 py-3 text-[13px] text-ink-faint">
                {t("models.noModelsFound")}
              </Text>
            ) : (
              <>
                {showRecents ? (
                  <ModelSection label={t("models.recentlyUsed")}>
                    {recents.map((model) => (
                      <ModelOption
                        key={`recent-${model.id}`}
                        model={model}
                        selected={model.id === value}
                        locked={!modelAllowedForPlan(model, plan)}
                        creditsLabel={t("chat.picker.credits", { credits: model.display_from_credits })}
                        visionLabel={t("models.vision")}
                        thinkingLabel={t("models.thinking")}
                        onSelect={() => selectModel(model, close)}
                      />
                    ))}
                  </ModelSection>
                ) : null}

                {groups.map((group) => (
                  <ModelSection key={group.origin} label={originLabel(group.origin)}>
                    {group.models.map((model) => (
                      <ModelOption
                        key={model.id}
                        model={model}
                        testId={`model-option-${model.id}`}
                        selected={model.id === value}
                        locked={!modelAllowedForPlan(model, plan)}
                        creditsLabel={t("chat.picker.credits", { credits: model.display_from_credits })}
                        visionLabel={t("models.vision")}
                        thinkingLabel={t("models.thinking")}
                        onSelect={() => selectModel(model, close)}
                      />
                    ))}
                  </ModelSection>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </Popover>
  );
}

function ModelSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-1 last:mb-0">
      <Text variant="label" as="div" className="px-3 pb-1 pt-2">
        {label}
      </Text>
      {children}
    </div>
  );
}

interface ModelOptionProps {
  model: ModelInfo;
  testId?: string;
  selected: boolean;
  /** The current plan can't use this model — show a lock; clicking opens the paywall. */
  locked: boolean;
  creditsLabel: string;
  visionLabel: string;
  thinkingLabel: string;
  onSelect: () => void;
}

/**
 * One model row. The full name sits on its own line so capability/plan badges
 * never squeeze it (the badges + credit estimate share the line below). A
 * locked model shows a lock in the leading slot instead of the check mark.
 */
function ModelOption({
  model,
  testId,
  selected,
  locked,
  creditsLabel,
  visionLabel,
  thinkingLabel,
  onSelect,
}: ModelOptionProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className="flex w-full items-start gap-2.5 rounded-sm px-3 py-2 text-left outline-none transition-colors duration-150 ease-house hover:bg-tint focus-visible:bg-tint"
    >
      <ProviderIcon origin={model.origin} className="mt-0.5 size-[18px] shrink-0" />
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate font-sans text-[13.5px] font-medium text-ink">
            {modelLabel(model)}
          </span>
          <span className="shrink-0 font-sans text-[11.5px] text-ink-faint">{creditsLabel}</span>
          {locked ? (
            <LockIcon className="size-4 shrink-0 text-ink-faint" />
          ) : (
            <CheckIcon
              className={cn("size-4 shrink-0 text-accent", selected ? "opacity-100" : "opacity-0")}
            />
          )}
        </span>
        {model.tags.includes("vision") || model.tags.includes("thinking") || !isFreeModel(model) ? (
          <span className="flex flex-wrap items-center gap-1.5">
            {model.tags.includes("vision") ? <Badge tone="violet">{visionLabel}</Badge> : null}
            {model.tags.includes("thinking") ? <Badge tone="amber">{thinkingLabel}</Badge> : null}
            {!isFreeModel(model) ? <Badge tone="ink">{model.plans[0]}</Badge> : null}
          </span>
        ) : null}
        {model.description ? (
          <span className="truncate font-sans text-[12.5px] text-ink-muted">{model.description}</span>
        ) : null}
      </span>
    </button>
  );
}
